import { SATOSHIS, toBitcoin } from 'lib/bsv-util'

export type BsvAmountUnit = 'bsv' | 'sats'

export type BsvAmountInputState =
    | { status: 'empty'; amountSats: '' }
    | { status: 'fractional-sats'; amountSats: '' }
    | { status: 'sub-satoshi-bsv'; amountSats: '' }
    | { status: 'invalid'; amountSats: '' }
    | { status: 'zero'; amountSats: 0 }
    | { status: 'valid'; amountSats: number }

const DECIMAL_AMOUNT_PATTERN = /^\d+(?:\.\d*)?$/

function decimalBsvToSats(value: string): number | '' {
    const [whole, rawFraction = ''] = value.split('.')
    const fraction = rawFraction.replace(/0+$/, '')
    if (fraction.length > 8) return ''

    const sats = BigInt(whole) * BigInt(SATOSHIS) + BigInt((fraction || '0').padEnd(8, '0'))
    if (sats > BigInt(Number.MAX_SAFE_INTEGER)) return ''

    return Number(sats)
}

function shiftDecimalAmount(value: string, places: number): string {
    const [whole, fraction = ''] = value.split('.')
    let digits = `${whole}${fraction}`
    let decimalIndex = whole.length + places

    if (decimalIndex <= 0) {
        digits = `${'0'.repeat(-decimalIndex)}${digits}`
        decimalIndex = 0
    } else if (decimalIndex >= digits.length) {
        digits = `${digits}${'0'.repeat(decimalIndex - digits.length)}`
    }

    const integerPart = (digits.slice(0, decimalIndex) || '0').replace(/^0+(?=\d)/, '')
    const fractionalPart = digits.slice(decimalIndex).replace(/0+$/, '')
    return fractionalPart ? `${integerPart}.${fractionalPart}` : integerPart
}

export function normalizeDecimalAmount(value: string | number): string {
    if (typeof value === 'string') return value.trim().replace(/^\.(?=\d)/, '0.')

    const text = String(value).toLowerCase()
    const [coefficient, exponent] = text.split('e')
    return exponent == null ? coefficient : shiftDecimalAmount(coefficient, Number(exponent))
}

function numericBsvToSats(value: number): number | '' {
    if (!Number.isFinite(value) || value < 0) return ''
    return decimalBsvToSats(normalizeDecimalAmount(value))
}

export function bsvAmountToSats(value: string | number, unit: BsvAmountUnit): number | '' {
    if (typeof value === 'number') {
        if (unit === 'sats') return Number.isSafeInteger(value) && value >= 0 ? value : ''
        return numericBsvToSats(value)
    }

    const normalizedValue = normalizeDecimalAmount(value)
    if (!normalizedValue || !DECIMAL_AMOUNT_PATTERN.test(normalizedValue)) return ''

    if (unit === 'sats') {
        if (normalizedValue.includes('.')) return ''
        const sats = Number(normalizedValue)
        return Number.isSafeInteger(sats) ? sats : ''
    }

    return decimalBsvToSats(normalizedValue)
}

export function bsvAmountInputToSats(value: string, unit: BsvAmountUnit): number | '' {
    return bsvAmountToSats(value, unit)
}

export function getBsvAmountInputState(value: string, unit: BsvAmountUnit): BsvAmountInputState {
    const normalizedValue = normalizeDecimalAmount(value)
    if (!normalizedValue) return { status: 'empty', amountSats: '' }

    const amountSats = bsvAmountInputToSats(normalizedValue, unit)
    if (amountSats === 0) return { status: 'zero', amountSats }
    if (amountSats !== '') return { status: 'valid', amountSats }

    const decimals = normalizedValue.split('.')[1] ?? ''
    if (unit === 'sats' && normalizedValue.includes('.')) {
        return { status: 'fractional-sats', amountSats: '' }
    }
    if (unit === 'bsv' && decimals.length > 8) {
        return { status: 'sub-satoshi-bsv', amountSats: '' }
    }

    return { status: 'invalid', amountSats: '' }
}

export function convertBsvAmountInputUnit(
    value: string | number,
    fromUnit: BsvAmountUnit,
    toUnit: BsvAmountUnit,
): string {
    if (fromUnit === toUnit) return String(value)

    const normalizedValue = normalizeDecimalAmount(value)
    if (!normalizedValue) return ''

    const sats = bsvAmountInputToSats(normalizedValue, fromUnit)
    if (sats !== '') return toUnit === 'bsv' ? toBitcoin(sats) : String(sats)

    if (!DECIMAL_AMOUNT_PATTERN.test(normalizedValue)) return ''

    // Preserve an invalid sub-satoshi value while switching units so the user can correct it.
    return shiftDecimalAmount(normalizedValue, fromUnit === 'bsv' ? 8 : -8)
}
